import {
  BoxGeometry,
  Color,
  DynamicDrawUsage,
  InstancedMesh,
  Matrix4,
  MeshBasicMaterial,
  Object3D,
  Vector3,
} from 'three';
import { rand } from '../util/math.js';

const STRIDE = 11;
const OFF = {
  px: 0, py: 1, pz: 2,
  vx: 3, vy: 4, vz: 5,
  life: 6, maxLife: 7, scale: 8,
  color: 9, alpha: 10,
};

const budgets = {
  low: 512,
  medium: 2048,
  high: 8192,
  ultra: 16384,
};

const presets = {
  muzzleFlash: { count: 8, ttl: 0.075, scale: [0.12, 0.32], speed: [1.4, 4.8], gravity: 0, alpha: 1 },
  bulletTrail: { count: 3, ttl: 0.14, scale: [0.045, 0.1], speed: [0.2, 0.7], gravity: 0, alpha: 0.55 },
  bloodSpray: { count: 12, ttl: 0.55, scale: [0.045, 0.12], speed: [2.2, 6.5], gravity: 10, alpha: 0.9 },
  sparks: { count: 10, ttl: 0.42, scale: [0.035, 0.08], speed: [3.0, 8.5], gravity: 12, alpha: 1 },
  smoke: { count: 6, ttl: 1.4, scale: [0.12, 0.34], speed: [0.25, 1.2], gravity: -0.7, alpha: 0.38 },
  shockwave: { count: 1, ttl: 0.42, scale: [0.7, 1.2], speed: [0, 0], gravity: 0, alpha: 0.75 },
  shellCasing: { count: 1, ttl: 4.0, scale: [0.08, 0.12], speed: [2.5, 4.8], gravity: 12, alpha: 1 },
  debris: { count: 16, ttl: 0.9, scale: [0.08, 0.2], speed: [1.8, 5.5], gravity: 9, alpha: 1 },
};

const geometry = new BoxGeometry(1, 1, 1);
const dummy = new Object3D();
const color = new Color();
const zeroMatrix = new Matrix4().makeScale(0, 0, 0);
const posScratch = new Vector3();

let particleState = null;

export function initParticles(world, options = {}) {
  const capacity = options.capacity ?? budgets.ultra;
  const mesh = new InstancedMesh(
    geometry,
    new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      vertexColors: true,
    }),
    capacity,
  );
  mesh.name = 'gpu-particles';
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  mesh.frustumCulled = false;
  world.particleRoot.add(mesh);

  particleState = {
    world,
    mesh,
    capacity,
    budget: budgets[options.quality || 'low'],
    quality: options.quality || 'low',
    data: new Float32Array(capacity * STRIDE),
    active: [],
    free: Array.from({ length: capacity }, (_, index) => capacity - 1 - index),
  };

  for (let i = 0; i < capacity; i += 1) mesh.setMatrixAt(i, zeroMatrix);
  mesh.instanceMatrix.needsUpdate = true;
  return particleState;
}

export function setParticleQuality(quality) {
  if (!particleState) return;
  particleState.quality = quality;
  particleState.budget = budgets[quality] || budgets.low;
}

export function spawn(type, position, options = {}) {
  const state = particleState;
  if (!state) return [];

  const preset = presets[type] || presets.sparks;
  const count = options.count ?? preset.count;
  const tint = options.color ?? 0x53ffb5;
  const spawned = [];

  for (let i = 0; i < count; i += 1) {
    if (state.active.length >= state.budget) break;
    const index = state.free.pop();
    if (index === undefined) break;

    writeParticle(state, index, type, position, tint, preset, options);
    state.active.push(index);
    spawned.push(index);
  }

  state.mesh.instanceMatrix.needsUpdate = true;
  if (state.mesh.instanceColor) state.mesh.instanceColor.needsUpdate = true;
  return spawned;
}

export function spawnParticles(position, tint = 0x53ffb5, count = 8, power = 1.2, options = {}) {
  return spawn(options.type || 'sparks', position, {
    ...options,
    color: tint,
    count,
    power,
  });
}

export function spawnMuzzleFlash(player, weapon) {
  const pos = posScratch.copy(player.pos).addScaledVector(player.aim, 1.05);
  pos.y = 1.13;
  return spawn('muzzleFlash', pos, {
    color: weapon.color || 0x53ffb5,
    count: weapon.id === 'shotgun' ? 12 : 6,
    direction: player.aim,
    power: weapon.id === 'rail' ? 1.8 : 1.1,
  });
}

export function spawnShellCasing(player, weapon) {
  const right = posScratch.set(player.aim.z, 0, -player.aim.x).normalize();
  const pos = player.pos.clone().addScaledVector(player.aim, 0.72).addScaledVector(right, 0.42);
  pos.y = 1.18;
  return spawn('shellCasing', pos, {
    color: weapon.id === 'rail' ? 0x6aa9ff : 0xd6b36d,
    direction: right,
    power: weapon.id === 'shotgun' ? 1.4 : 1,
  });
}

export function updateParticles(dt) {
  const state = particleState;
  if (!state) return;

  for (let cursor = state.active.length - 1; cursor >= 0; cursor -= 1) {
    const index = state.active[cursor];
    const base = index * STRIDE;
    const maxLife = state.data[base + OFF.maxLife];
    state.data[base + OFF.life] += dt;

    if (state.data[base + OFF.life] >= maxLife) {
      releaseParticle(state, cursor, index);
      continue;
    }

    const gravity = state.data[base + OFF.alpha];
    state.data[base + OFF.vy] -= gravity * dt;
    state.data[base + OFF.px] += state.data[base + OFF.vx] * dt;
    state.data[base + OFF.py] += state.data[base + OFF.vy] * dt;
    state.data[base + OFF.pz] += state.data[base + OFF.vz] * dt;
    if (state.data[base + OFF.py] < 0.02) {
      state.data[base + OFF.py] = 0.02;
      state.data[base + OFF.vy] *= -0.16;
      state.data[base + OFF.vx] *= 0.68;
      state.data[base + OFF.vz] *= 0.68;
    }

    renderParticle(state, index);
  }

  state.mesh.instanceMatrix.needsUpdate = true;
  if (state.mesh.instanceColor) state.mesh.instanceColor.needsUpdate = true;
}

export function clearParticles() {
  const state = particleState;
  if (!state) return;
  for (let cursor = state.active.length - 1; cursor >= 0; cursor -= 1) {
    releaseParticle(state, cursor, state.active[cursor]);
  }
  state.mesh.instanceMatrix.needsUpdate = true;
}

function writeParticle(state, index, type, position, tint, preset, options) {
  const base = index * STRIDE;
  const power = options.power ?? 1;
  const dir = options.direction;
  const speed = rand(preset.speed[0], preset.speed[1]) * power;
  const angle = Math.random() * Math.PI * 2;
  const spread = type === 'bulletTrail' && dir ? 0.08 : 1;
  const vx = dir ? dir.x * speed + rand(-spread, spread) : Math.cos(angle) * speed;
  const vz = dir ? dir.z * speed + rand(-spread, spread) : Math.sin(angle) * speed;

  state.data[base + OFF.px] = position.x + rand(-0.12, 0.12);
  state.data[base + OFF.py] = options.y ?? position.y ?? rand(0.35, 1.8);
  state.data[base + OFF.pz] = position.z + rand(-0.12, 0.12);
  state.data[base + OFF.vx] = vx;
  state.data[base + OFF.vy] = rand(0.4, 2.4) * power;
  state.data[base + OFF.vz] = vz;
  state.data[base + OFF.life] = 0;
  state.data[base + OFF.maxLife] = options.ttl ?? preset.ttl;
  state.data[base + OFF.scale] = rand(preset.scale[0], preset.scale[1]) * power;
  state.data[base + OFF.color] = tint;
  state.data[base + OFF.alpha] = options.gravity ?? preset.gravity;

  color.setHex(tint);
  state.mesh.setColorAt(index, color);
  renderParticle(state, index);
}

function renderParticle(state, index) {
  const base = index * STRIDE;
  const t = state.data[base + OFF.life] / Math.max(0.001, state.data[base + OFF.maxLife]);
  const scale = state.data[base + OFF.scale] * Math.max(0.001, 1 - t);
  dummy.position.set(
    state.data[base + OFF.px],
    state.data[base + OFF.py],
    state.data[base + OFF.pz],
  );
  dummy.scale.setScalar(scale);
  dummy.rotation.set(t * 6.1, t * 4.7, t * 3.3);
  dummy.updateMatrix();
  state.mesh.setMatrixAt(index, dummy.matrix);
}

function releaseParticle(state, cursor, index) {
  state.mesh.setMatrixAt(index, zeroMatrix);
  state.active.splice(cursor, 1);
  state.free.push(index);
}
